/**
 * Short-lived read cache for the small lists every page reads (golive-review
 * G10: ~100 people opening the app at 9 am each re-read stores, templates
 * and compliance clocks on every page). Opt-in per query (`cacheMs`), per
 * tab, in memory only — never audit data at rest. Any write to a list from
 * this tab drops its entries, so the person who changes something sees it
 * at once; other people see it within the cache time.
 */
interface Entry {
  at: number;
  ms: number;
  value: Promise<unknown>;
}

const entries = new Map<string, Entry>();

export function cachedRead<T>(list: string, key: string, ms: number, load: () => Promise<T>, now = Date.now()): Promise<T> {
  const id = `${list}\u0000${key}`;
  const hit = entries.get(id);
  if (hit && now - hit.at < hit.ms) return hit.value as Promise<T>;
  // Expired entries go when anything new is stored (QA 2026-10-22: the
  // hour-rounded audits window made a new key every hour in an all-day tab).
  for (const [k, e] of entries) if (now - e.at >= e.ms) entries.delete(k);
  const value = load();
  const entry = { at: now, ms, value };
  entries.set(id, entry);
  // Never keep a failure.
  value.catch(() => {
    if (entries.get(id) === entry) entries.delete(id);
  });
  return value;
}

/** A write to `list` happened (or another tab's change must show): forget its reads. */
export function invalidateList(list: string): void {
  const prefix = `${list}\u0000`;
  for (const id of [...entries.keys()]) if (id.startsWith(prefix)) entries.delete(id);
}

export function clearReadCache(): void {
  entries.clear();
}
